import { GOOGLE_ADS_TAG_ID, GOOGLE_ANALYTICS_ID, GOOGLE_TAG_MANAGER_ID } from "core/config/config";
import { useEffect, useRef } from "react";
import TagManager from "react-gtm-module";

import { useCookieContext } from "core/contexts/CookieContext";
import { removeUnconsentedCookies } from "core/includes/cookies";
import { useAppState } from "core/contexts/AppContext";
import { FLAG_COOKIE_SETTINGS } from "core/includes/featureFlags";
import { hasFeatureFlag } from "core/includes/site";
import window, { hasWindow } from 'core/includes/window';

const useGoogleTagsIntegration = () => {
    const { consentedCategories } = useCookieContext();
    const documentScriptElements = document.getElementsByTagName('script');
    const isMainScriptSet = useRef(false);
    const [appState] = useAppState();
    const hasCookieSettingsFlag = hasFeatureFlag(appState.featureFlags, FLAG_COOKIE_SETTINGS);

    useEffect(function toggleGoogleTagsScripts() {
        if (!hasWindow) return;

        const isGoogleTagsScriptsAllowed =
            (!hasCookieSettingsFlag && !appState.isLoadingFeatureFlags) ||
            consentedCategories.some(category => category.isAnalytics)
        ;

        if (isGoogleTagsScriptsAllowed && !isMainScriptSet.current) {
            // Initialise the main google tag manager script
            TagManager.initialize({ gtmId: GOOGLE_TAG_MANAGER_ID });
            isMainScriptSet.current = true;

            // Prepare gtag stuff
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () {
                window.dataLayer?.push(arguments);
            }
            window.gtag('js', new Date());

            // Configure tags for analytics and ads
            for (const googleTagId of [GOOGLE_ANALYTICS_ID, GOOGLE_ADS_TAG_ID]) {
                window.gtag('config', googleTagId);
            }
            // Configure manual page view
            window.gtag(
                'event',
                'page_view',
                {
                    page_path: window.location?.pathname + window.location?.search,
                },
            );
        } else if (!isGoogleTagsScriptsAllowed) {
            // Remove all scripts to do with google tag manager
            for (let i = 0; i < documentScriptElements.length; i++) {
                const script = documentScriptElements[i];
                if (
                    script &&
                    (script.src.includes('googletagmanager.com') || script.innerText.includes('googletagmanager.com'))
                ) {
                    script.remove();
                }
            }
            isMainScriptSet.current = false;

            // Remove all attributes to do with google tag manager
            window.dataLayer = null;
            window.gtag = null;

            // Sanity check to remove cookies
            removeUnconsentedCookies();
        }
    }, [hasCookieSettingsFlag, appState.isLoadingFeatureFlags, consentedCategories, documentScriptElements]);
}

export default useGoogleTagsIntegration;